import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import './LabelGeneratorFormContent.less';
import {
  Form, Input, Select, Avatar, Typography, Tooltip, InputNumber, Checkbox, Skeleton, Row, Col,
} from 'antd';
import { DatePicker, HoverHelp } from 'components';
import { capitalCase } from 'change-case';
import { resolveLabelBarcode } from 'util/label-barcode-resolve';

const { Text } = Typography;

const { Option } = Select;

const LabelGeneratorFormContent = ({
  type,
  products,
  form,
  modalOpen,
  /** When set, unit size is fixed (New Label Builder) — no dropdown. */
  lockedUnitSize,
  /** When type is pallet and set, pallet-of is fixed. */
  lockedPalletType,
  /** Shown next to locked unit size (optional friendly text). */
  unitSizeDisplayLabel,
  /** New Label Builder: 150px tap badge row (matches Product Editor About tab). */
  largeTapBadge,
  /** New Label Builder: single block for detected format + unit size (hides duplicate unit row). */
  mergeDetectedLabelFormat,
  /** Preformatted line, e.g. "Slab — 24 x 440ml Cans" or "Pallet — Case — …". */
  detectedLabelSummary,
  showPalletPrintOption,
  palletMode,
  onPalletModeChange,
  palletOfPlural,
  /** New Label Builder: Huaxi (China distributor) slab variant toggle. */
  showHuaxiOption,
  huaxiMode,
  onHuaxiModeChange,
  /** New Label Builder: hide UI; submit certifiedFresh as false. */
  hideCertifiedFresh,
  /** New Label Builder side-by-side preview: tighter 2-column form grid. */
  compactLayout,
}) => {
  // Label generators are all single selection type, so
  // the products array should only have one item
  const [product] = products;

  const [unitSizeWarning, setUnitSizeWarning] = useState(null);
  const showUnitBarcode = Form.useWatch('showUnitBarcode', form);

  // Keeping track of the selected unit type allows controlling which
  // date types are shown when generating pallet labels.
  // For example, don't show 'canned on' when 'case' is selected.
  const [selectedPalletType, setSelectedPalletType] = useState(null);
  // Change the date type to the correct alternative if a conflicting
  // unit type has been selected. For example, pallet of slabs changed
  // to pallet of cases, so 'canned on' becomes 'bottled on'.
  const onPalletTypeChanged = (palletType) => {
    setSelectedPalletType(palletType);
    form.resetFields();
    form.setFieldsValue({ palletType });

    const dateType = form.getFieldValue('dateType');

    if (palletType === 'slab' && ['bottled', 'kegged'].includes(dateType)) {
      form.setFieldsValue({ dateType: 'canned' });
    } else if (palletType === 'case' && ['canned', 'kegged'].includes(dateType)) {
      form.setFieldsValue({ dateType: 'bottled' });
    } else if (palletType === 'keg' && ['bottled', 'canned'].includes(dateType)) {
      form.setFieldsValue({ dateType: 'kegged' });
    }

    // Clear unit size when unit type changes
    form.setFieldsValue({ unitSize: null });
    setUnitSizeWarning(null);
  };

  // Check and show a warning if a barcode does not exist for the selected unit size.
  // Kegs only warn when the unit-barcode toggle is on.
  const updateBarcodeWarning = (unitSize, wantUnitBarcode) => {
    if (!unitSize || !product) {
      setUnitSizeWarning(null);
      return;
    }
    const palletType = form.getFieldValue('palletType');
    const barcodeType = type === 'pallet' ? palletType : type;
    const legacyKey = `barcode${capitalCase(barcodeType)}.${unitSize}`;
    const fromSku = resolveLabelBarcode(product, {
      labelType: type,
      unitSize,
      palletType,
    });
    const hasBarcode = !!(product[legacyKey] || fromSku);
    const kegOptional = barcodeType === 'keg' && !wantUnitBarcode;
    if (hasBarcode || kegOptional) {
      setUnitSizeWarning(null);
      return;
    }
    setUnitSizeWarning(barcodeType === 'keg'
      ? 'Warning: This SKU has no unit barcode'
      : `Warning: A barcode has not been added for ${barcodeType}s of this unit size`);
  };

  const onUnitSizeChanged = (unitSize) => {
    updateBarcodeWarning(unitSize, form.getFieldValue('showUnitBarcode'));
  };

  // Clear selected unit types and warnings when the modal closes
  useEffect(() => {
    if (!modalOpen) {
      setSelectedPalletType(null);
      setUnitSizeWarning(null);
    }
  }, [modalOpen]);

  // New Label Builder: apply locked unit / pallet and refresh barcode warning
  useEffect(() => {
    if (!product || !lockedUnitSize) return undefined;
    const next = { unitSize: lockedUnitSize };
    if (type === 'pallet' && lockedPalletType) {
      next.palletType = lockedPalletType;
      setSelectedPalletType(lockedPalletType);
    }
    form.setFieldsValue(next);
    const t = setTimeout(() => onUnitSizeChanged(lockedUnitSize), 0);
    return () => clearTimeout(t);
  }, [type, product, lockedUnitSize, lockedPalletType, form]);

  useEffect(() => {
    if (type !== 'keg' || !product) return undefined;
    updateBarcodeWarning(form.getFieldValue('unitSize'), showUnitBarcode);
    return undefined;
  }, [type, product, showUnitBarcode, form]);

  if (!product) {
    return null;
  }

  const showLockedPalletRow = type === 'pallet' && lockedPalletType && !mergeDetectedLabelFormat;

  const renderMergeHeader = mergeDetectedLabelFormat && lockedUnitSize;

  return (
    <div className={compactLayout ? 'label-generator-form--compact' : undefined}>
      {renderMergeHeader ? (
        <Row gutter={[16, 16]} className="label-generator-header-row" align="top">
          <Col
            xs={24}
            sm={compactLayout ? 7 : 8}
            md={compactLayout ? 6 : 6}
            lg={compactLayout ? 6 : 5}
            xl={compactLayout ? 6 : 4}
            className="label-generator-header-badge-col"
          >
            {largeTapBadge ? (
              <div className="label-generator-tap-badge-large">
                {product.tapBadgeUrl ? (
                  <Avatar
                    src={product.tapBadgeUrl}
                    alt={product.name || 'Tap badge'}
                    size={150}
                    shape="circle"
                  />
                ) : (
                  <Skeleton.Avatar active size={150} shape="circle" />
                )}
              </div>
            ) : (
              <Avatar src={product.tapBadgeUrl} size={64} className="label-generator-header-avatar" />
            )}
          </Col>
          <Col
            xs={24}
            sm={compactLayout ? 17 : 16}
            md={compactLayout ? 18 : 18}
            lg={compactLayout ? 18 : 19}
            xl={compactLayout ? 18 : 20}
            className="label-generator-header-fields-col"
          >
            {compactLayout ? (
              <>
                <Form.Item
                  name="customProductName"
                  label="Product Name"
                  value={product.name}
                  initialValue={product.name}
                  rules={[{ required: true, message: 'Name is required' }]}
                >
                  <Input defaultValue={product.name} />
                </Form.Item>
                <Form.Item
                  className="unit-size-selection label-generator-detected-format-form-item"
                  label="Label format"
                  help={unitSizeWarning}
                  required
                >
                  <div className="label-generator-detected-format-block">
                    <Text strong className="label-generator-detected-format-value">
                      {detectedLabelSummary || unitSizeDisplayLabel || lockedUnitSize}
                    </Text>
                    {showPalletPrintOption && typeof onPalletModeChange === 'function' && (
                      <div className="label-generator-pallet-print-row">
                        <Checkbox
                          checked={palletMode}
                          onChange={(e) => onPalletModeChange(e.target.checked)}
                        >
                          Print as pallet label (pallet of
                          {' '}
                          {palletOfPlural}
                          )
                        </Checkbox>
                      </div>
                    )}
                    {showHuaxiOption && typeof onHuaxiModeChange === 'function' && (
                      <div className="label-generator-pallet-print-row">
                        <Checkbox
                          checked={huaxiMode}
                          onChange={(e) => onHuaxiModeChange(e.target.checked)}
                        >
                          Huaxi (China) variant
                        </Checkbox>
                      </div>
                    )}
                  </div>
                  <Form.Item
                    name="unitSize"
                    noStyle
                    rules={[{ required: true, message: 'Unit size is required' }]}
                    initialValue={lockedUnitSize}
                  >
                    <Input type="hidden" />
                  </Form.Item>
                </Form.Item>
              </>
            ) : (
              <Row gutter={[16, 16]}>
                <Col xs={24} md={12} xl={14}>
                  <Form.Item
                    name="customProductName"
                    label="Product Name"
                    value={product.name}
                    initialValue={product.name}
                    rules={[{ required: true, message: 'Name is required' }]}
                  >
                    <Input defaultValue={product.name} />
                  </Form.Item>
                </Col>
                <Col xs={24} md={12} xl={10}>
                  <Form.Item
                    className="unit-size-selection label-generator-detected-format-form-item"
                    label="Label format"
                    help={unitSizeWarning}
                    required
                  >
                    <div className="label-generator-detected-format-block">
                      <Text strong className="label-generator-detected-format-value">
                        {detectedLabelSummary || unitSizeDisplayLabel || lockedUnitSize}
                      </Text>
                      {showPalletPrintOption && typeof onPalletModeChange === 'function' && (
                        <div className="label-generator-pallet-print-row">
                          <Checkbox
                            checked={palletMode}
                            onChange={(e) => onPalletModeChange(e.target.checked)}
                          >
                            Print as pallet label (pallet of
                            {' '}
                            {palletOfPlural}
                            )
                          </Checkbox>
                        </div>
                      )}
                      {showHuaxiOption && typeof onHuaxiModeChange === 'function' && (
                        <div className="label-generator-pallet-print-row">
                          <Checkbox
                            checked={huaxiMode}
                            onChange={(e) => onHuaxiModeChange(e.target.checked)}
                          >
                            Huaxi (China) variant
                          </Checkbox>
                        </div>
                      )}
                    </div>
                    <Form.Item
                      name="unitSize"
                      noStyle
                      rules={[{ required: true, message: 'Unit size is required' }]}
                      initialValue={lockedUnitSize}
                    >
                      <Input type="hidden" />
                    </Form.Item>
                  </Form.Item>
                </Col>
              </Row>
            )}
          </Col>
        </Row>
      ) : (
        <div className={`label-generator-product-name ${largeTapBadge ? 'label-generator-product-name--with-large-badge' : ''}`}>
          {largeTapBadge ? (
            <div className="label-generator-tap-badge-large">
              {product.tapBadgeUrl ? (
                <Avatar
                  src={product.tapBadgeUrl}
                  alt={product.name || 'Tap badge'}
                  size={150}
                  shape="circle"
                />
              ) : (
                <Skeleton.Avatar active size={150} shape="circle" />
              )}
            </div>
          ) : (
            <Avatar src={product.tapBadgeUrl} className="generator-modal-product-avatar" />
          )}
          <div className="label-generator-product-name-fields">
            <Form.Item
              name="customProductName"
              label="Product Name"
              value={product.name}
              initialValue={product.name}
              rules={[{ required: true, message: 'Name is required' }]}
            >
              <Input defaultValue={product.name} />
            </Form.Item>
          </div>
        </div>
      )}
      {type === 'pallet' && (
        lockedPalletType ? (
          <>
            <Form.Item name="palletType" hidden initialValue={lockedPalletType}>
              <Input type="hidden" />
            </Form.Item>
            {showLockedPalletRow && (
              <div className="label-generator-locked-field" style={{ marginBottom: 16 }}>
                <Text type="secondary">Pallet of</Text>
                <div>
                  <Text strong>
                    {lockedPalletType === 'slab' ? 'Slabs' : null}
                    {lockedPalletType === 'case' ? 'Cases' : null}
                    {lockedPalletType === 'keg' ? 'Kegs' : null}
                  </Text>
                </div>
              </div>
            )}
          </>
        ) : (
          <Form.Item
            name="palletType"
            label="Pallet of"
            rules={[{ required: true, message: 'Pallet type is required' }]}
          >
            <Select onChange={(palletType) => onPalletTypeChanged(palletType)}>
              <Option value="slab">Slabs</Option>
              <Option value="case">Cases</Option>
              <Option value="keg">Kegs</Option>
            </Select>
          </Form.Item>
        )
      )}
      {!renderMergeHeader ? (
        <Tooltip
          title={(type === 'pallet' && !selectedPalletType && !lockedPalletType) ? (
            'Select what the pallet contains first using the field above'
          ) : null}
          placement="bottom"
        >
          <Form.Item
            className="unit-size-selection"
            label="Unit size"
            help={unitSizeWarning}
            required
          >
            {lockedUnitSize ? (
              <>
                <Form.Item
                  name="unitSize"
                  noStyle
                  rules={[{ required: true, message: 'Unit size is required' }]}
                  initialValue={lockedUnitSize}
                >
                  <Input type="hidden" />
                </Form.Item>
                <Text>{unitSizeDisplayLabel || lockedUnitSize}</Text>
              </>
            ) : (
              <Form.Item
                name="unitSize"
                noStyle
                rules={[{ required: true, message: 'Unit size is required' }]}
              >
                <Select
                  disabled={(type === 'pallet' && !selectedPalletType && !lockedPalletType)}
                  onChange={onUnitSizeChanged}
                >
                  {[selectedPalletType, type].includes('slab') && (
                  <>
                    <Option value="24x.440">24 x 440ml Cans</Option>
                    <Option value="440.12">12 x 440ml Cans</Option>
                    <Option value="15x.440">15 x 440ml Cans</Option>
                    <Option value="16x.440">16 x 440ml Cans</Option>
                    <Option value="440.4">440ml Cans (6 x 4-packs)</Option>
                    <Option value="375">375ml Cans</Option>
                    <Option value="330.12">330ml Cans (2 x 12-packs)</Option>
                    <Option value="330.10">330ml Cans (3 x 10-packs)</Option>
                    <Option value="330.6">330ml Cans (4 x 6-packs)</Option>
                    <Option value="330.4">330ml Cans (6 x 4-packs)</Option>
                    <Option value="24x.330">24 x 330ml Cans</Option>
                    <Option value="16x.330">16 x 330ml Cans</Option>
                    <Option value="250.4">250ml Cans (6 x 4-packs)</Option>
                    <Option value="250">250ml Cans</Option>
                  </>
                  )}
                  {[selectedPalletType, type].includes('case') && (
                  <>
                    <Option value="750.12">750ml Bottles (Crushed 12-pack)</Option>
                    <Option value="750">750ml Bottles</Option>
                    <Option value="650">650ml Bottles</Option>
                    <Option value="375.6">375ml Bottles (4 x 6-packs)</Option>
                    <Option value="375">375ml Bottles</Option>
                    <Option value="330.6">330ml Bottles (4 x 6-packs)</Option>
                    <Option value="330">330ml Bottles</Option>
                  </>
                  )}
                  {[selectedPalletType, type].includes('keg') && (
                  <>
                    <Option value="50">50L</Option>
                    <Option value="30">30L</Option>
                    <Option value="20">20L</Option>
                    <Option value="19.5">19.5L</Option>
                  </>
                  )}
                </Select>
              </Form.Item>
            )}
          </Form.Item>
        </Tooltip>
      ) : null}

      {hideCertifiedFresh ? (
        <Form.Item name="certifiedFresh" valuePropName="checked" initialValue={false} style={{ display: 'none' }}>
          <Checkbox />
        </Form.Item>
      ) : (
        <Form.Item
          name="certifiedFresh"
          valuePropName="checked"
          className="certified-fresh-checkbox"
          labelCol={{ span: 24 }}
        >
          <Checkbox>
            Certified Fresh
          </Checkbox>
        </Form.Item>
      )}

      {type === 'keg' && (
        <Form.Item
          name="showUnitBarcode"
          valuePropName="checked"
          initialValue={false}
          className="certified-fresh-checkbox"
          labelCol={{ span: 24 }}
        >
          <Checkbox>
            Unit barcode
            <HoverHelp message="Prints the SKU unit barcode on the right of the keg sticker." />
          </Checkbox>
        </Form.Item>
      )}

      <Row gutter={[16, 16]} className="label-generator-details-grid-row" align="top">
        <Col xs={24} sm={12} md={compactLayout ? 12 : 6} lg={compactLayout ? 12 : 5} xl={compactLayout ? 12 : 4}>
          <Form.Item
            name="gyle"
            label="Gyle"
            rules={[{ required: true, message: 'Gyle is required' }]}
          >
            <Input className="label-generator-input-gyle" maxLength={12} />
          </Form.Item>
        </Col>
        <Col xs={24} sm={12} md={compactLayout ? 12 : 6} lg={compactLayout ? 12 : 5} xl={compactLayout ? 12 : 4}>
          <Form.Item
            name="quantity"
            rules={type === 'pallet'
              ? undefined
              : [{ required: true, message: 'Number of labels is required' }]}
            label={(
              <>
                {type === 'pallet' ? `Quantity${selectedPalletType ? ` of ${selectedPalletType}s` : ''}` : 'Number of labels'}
                <HoverHelp message={
                    type === 'pallet' ? (
                      'Number shown as quantity on the label. Leave blank to print ___ for a write-in.'
                    ) : (
                      'This is the minimum number of labels required and may be rounded up to fill the nearest full page.')
                    }
                />
              </>
                )}
          >
            <InputNumber placeholder={1} className="label-generator-input-quantity" min={1} />
          </Form.Item>
        </Col>
        <Col xs={24} sm={12} md={compactLayout ? 12 : 6} lg={compactLayout ? 12 : 5} xl={compactLayout ? 12 : 6}>
          <Form.Item
            name="dateType"
            label="Date type"
            rules={[{ required: true, message: 'Date type is required' }]}
            className="label-generator-date-type-item"
          >
            <Select
              disabled={huaxiMode}
              onDropdownVisibleChange={() => (
                setSelectedPalletType(form.getFieldValue('palletType')))}
            >
              {huaxiMode ? (
                <Option value="packed">Packed on:</Option>
              ) : (
                <>
                  { [selectedPalletType, type].includes('slab') && <Option value="canned">Canned on:</Option>}
                  { [selectedPalletType, type].includes('case') && <Option value="bottled">Bottled on:</Option>}
                  { [selectedPalletType, type].includes('keg') && <Option value="kegged">Kegged on:</Option>}
                  { ![selectedPalletType, type].includes('keg') && <Option value="bestBefore">Best before:</Option>}
                  <Option value="packed">Packed on:</Option>
                  { ![selectedPalletType, type].includes('keg') && <Option value="expiry">Expiry:</Option>}
                  <Option value="useBy">Use by:</Option>
                </>
              )}
            </Select>
          </Form.Item>
        </Col>
        <Col xs={24} sm={12} md={compactLayout ? 12 : 6} lg={compactLayout ? 12 : 9} xl={compactLayout ? 12 : 10}>
          <Form.Item
            name="date"
            label="Date"
            rules={[{ required: true, message: 'A date is required' }]}
            className="label-generator-date-picker-item"
          >
            <DatePicker
              picker="day"
              placeholder={null}
              style={{ width: '100%' }}
              format="DD/MM/YYYY"
            />
          </Form.Item>
        </Col>
      </Row>
    </div>
  );
};

LabelGeneratorFormContent.propTypes = {
  type: PropTypes.oneOf(['keg', 'case', 'slab', 'pallet']).isRequired,
  products: PropTypes.arrayOf(PropTypes.object).isRequired,
  // Disabled since ant form shape may change
  // eslint-disable-next-line react/forbid-prop-types
  form: PropTypes.object.isRequired,
  modalOpen: PropTypes.bool.isRequired,
  lockedUnitSize: PropTypes.string,
  lockedPalletType: PropTypes.oneOf(['slab', 'case', 'keg']),
  unitSizeDisplayLabel: PropTypes.string,
  largeTapBadge: PropTypes.bool,
  mergeDetectedLabelFormat: PropTypes.bool,
  detectedLabelSummary: PropTypes.string,
  showPalletPrintOption: PropTypes.bool,
  palletMode: PropTypes.bool,
  onPalletModeChange: PropTypes.func,
  palletOfPlural: PropTypes.string,
  showHuaxiOption: PropTypes.bool,
  huaxiMode: PropTypes.bool,
  onHuaxiModeChange: PropTypes.func,
  hideCertifiedFresh: PropTypes.bool,
  compactLayout: PropTypes.bool,
};

LabelGeneratorFormContent.defaultProps = {
  lockedUnitSize: undefined,
  lockedPalletType: undefined,
  unitSizeDisplayLabel: undefined,
  largeTapBadge: false,
  mergeDetectedLabelFormat: false,
  detectedLabelSummary: undefined,
  showPalletPrintOption: false,
  palletMode: false,
  onPalletModeChange: undefined,
  palletOfPlural: '',
  showHuaxiOption: false,
  huaxiMode: false,
  onHuaxiModeChange: undefined,
  hideCertifiedFresh: false,
  compactLayout: false,
};

export default LabelGeneratorFormContent;
